/**
 * Dashboard Page – Übersicht (Support-Metriken & Performance)
 * Modernes, helles Layout. Charts via recharts.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Ticket,
  Mail,
  AlertOctagon,
  Clock,
  Smile,
  ArrowUpRight,
  ArrowDownRight,
  ArrowRight,
  MoreHorizontal,
  Info,
  Calendar,
  ChevronDown,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import dashboardService from '@/services/dashboard.service';
import ticketsService from '@/services/tickets.service';
import { transformApiTicketToFrontend } from '@/lib/ticketUtils';
import { formatNumber } from '@/lib/format';

const BLUE = '#34383f';
const GREEN = '#43a776';

// HINWEIS: die deutschen Werte hier entsprechen exakt dem bisherigen Stand
// (inkl. bekannter Eigenheiten: 'solved' zeigte ebenfalls "Geschlossen",
// die Prioritäten waren englisch). Deutsch bleibt damit unverändert;
// die übrigen Sprachen sind korrekt übersetzt.
const STATUS_META: Record<string, { key: string; label: string; color: string }> = {
  new: { key: 'domain:status.new', label: 'Neu', color: '#8b919b' },
  open: { key: 'domain:status.open', label: 'Offen', color: '#43a776' },
  in_progress: { key: 'domain:status.in_progress', label: 'In Bearbeitung', color: '#8170b7' },
  pending: { key: 'domain:status.pending', label: 'Wartend', color: '#d69a45' },
  solved: { key: 'dashboard:statusMeta.solved', label: 'Geschlossen', color: '#68707b' },
  closed: { key: 'domain:status.closed', label: 'Geschlossen', color: '#68707b' },
};

const PRIORITY_META: Record<string, { key: string; label: string; color: string }> = {
  urgent: { key: 'dashboard:priorityMeta.urgent', label: 'Urgent', color: '#dc5d57' },
  high: { key: 'dashboard:priorityMeta.high', label: 'High', color: '#d69a45' },
  normal: { key: 'dashboard:priorityMeta.normal', label: 'Normal', color: '#747b85' },
  low: { key: 'dashboard:priorityMeta.low', label: 'Low', color: '#43a776' },
};

const PRIORITY_BADGE: Record<string, string> = {
  urgent: 'dashboard-priority dashboard-priority--urgent',
  high: 'dashboard-priority dashboard-priority--high',
  normal: 'dashboard-priority dashboard-priority--normal',
  low: 'dashboard-priority dashboard-priority--low',
};
const PRIORITY_LABEL: Record<string, { key: string; label: string }> = {
  urgent: { key: 'dashboard:priorityLabel.urgent', label: 'Dringend' },
  high: { key: 'dashboard:priorityLabel.high', label: 'High' },
  normal: { key: 'dashboard:priorityLabel.normal', label: 'Normal' },
  low: { key: 'dashboard:priorityLabel.low', label: 'Low' },
};

const fmtMinutes = (m: number | null) => {
  if (!m) return 'N/A';
  if (m < 60) return `${Math.round(m)}m`;
  return `${Math.floor(m / 60)}h ${Math.round(m % 60)}m`;
};
const fmtNum = (n: number) => formatNumber(n ?? 0);
const ago = (d: string) => {
  const min = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h}h`;
  return `vor ${Math.floor(h / 24)}d`;
};

function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('30');
  const days = parseInt(period);

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['dashboard-overview', period],
    queryFn: () => dashboardService.getOverview(days),
    staleTime: 5 * 60 * 1000,
  });
  const { data: trends } = useQuery({
    queryKey: ['dashboard-trends', period],
    queryFn: () => dashboardService.getTicketTrends(days, 'day'),
    staleTime: 5 * 60 * 1000,
  });
  const { data: agents } = useQuery({
    queryKey: ['dashboard-agents', period],
    queryFn: () => dashboardService.getAgentPerformance(days),
    staleTime: 5 * 60 * 1000,
  });
  const { data: statusDist } = useQuery({
    queryKey: ['dashboard-status', period],
    queryFn: () => dashboardService.getStatusDistribution(days),
    staleTime: 5 * 60 * 1000,
  });
  const { data: priorityDist } = useQuery({
    queryKey: ['dashboard-priority', period],
    queryFn: () => dashboardService.getPriorityDistribution(days),
    staleTime: 5 * 60 * 1000,
  });
  const { data: systemTags } = useQuery({
    queryKey: ['dashboard-system-tags', period],
    queryFn: () => dashboardService.getSystemTagAnalytics(days, 6),
    staleTime: 5 * 60 * 1000,
  });
  const { data: recent } = useQuery({
    queryKey: ['dashboard-recent-tickets'],
    queryFn: () => ticketsService.getTickets({ page: 1, limit: 5 } as any),
    staleTime: 2 * 60 * 1000,
  });

  const tk = overview?.tickets;
  const safeTrends = Array.isArray(trends)
    ? trends.map((i: any) => ({ period: String(i?.period || ''), created: Number(i?.created) || 0, resolved: Number(i?.resolved) || 0, urgent: Number(i?.urgent) || 0 }))
    : [];
  // "Geschlossen" (closed/solved) donut'ta gösterilmez — ezici çoğunluğu diğer
  // statusların payını görünmez kılıyor. Yüzdeler kalan görünür toplam üzerinden hesaplanır.
  const safeStatus = Array.isArray(statusDist)
    ? statusDist
        .filter((i: any) => i?.status && i.status !== 'closed' && i.status !== 'solved')
        .map((i: any) => ({ key: String(i.status), count: Number(i.count) || 0, pct: Number(i.percentage) || 0 }))
    : [];
  const safePriority = Array.isArray(priorityDist)
    ? priorityDist.filter((i: any) => i?.priority).map((i: any) => ({ key: String(i.priority), count: Number(i.count) || 0, pct: Number(i.percentage) || 0 }))
    : [];
  const safeTags = Array.isArray(systemTags?.systemTags)
    ? systemTags!.systemTags.filter((x: any) => !x.parent_id).slice(0, 4).map((x: any) => ({ id: x.id, name: x.name, count: Number(x.ticket_count) || 0, pct: Number(x.percentage) || 0 }))
    : [];
  const safeAgents = Array.isArray(agents) ? agents.slice(0, 4) : [];
  const recentTickets = (() => {
    const arr = (recent as any)?.data?.tickets;
    if (!Array.isArray(arr)) return [];
    try { return arr.slice(0, 4).map((x: any) => transformApiTicketToFrontend(x)); } catch { return []; }
  })();

  const statusTotal = safeStatus.reduce((s, x) => s + x.count, 0);
  const priorityTotal = safePriority.reduce((s, x) => s + x.count, 0);
  const maxTag = safeTags[0]?.count || 1;

  const trendPct = (key: 'created' | 'urgent') => {
    if (safeTrends.length < 4) return null;
    const half = Math.floor(safeTrends.length / 2);
    const a = safeTrends.slice(0, half).reduce((s, x) => s + (x[key] || 0), 0);
    const b = safeTrends.slice(half).reduce((s, x) => s + (x[key] || 0), 0);
    if (!a) return null;
    return Math.round(((b - a) / a) * 100);
  };

  const openTotal = (Number(tk?.open_tickets) || 0) + (Number(tk?.new_tickets) || 0) + (Number(tk?.in_progress_tickets) || 0);

  const kpis = [
    { title: 'Total Tickets', value: fmtNum(Number(tk?.total_tickets) || 0), tile: 'dashboard-kpi-icon--ink', icon: Ticket, pct: trendPct('created'), invert: false },
    { title: 'Offene Anfragen', value: fmtNum(openTotal), tile: 'dashboard-kpi-icon--green', icon: Mail, pct: trendPct('created'), invert: true },
    { title: t('domain:priority.urgent', 'Dringend'), value: fmtNum(Number(tk?.urgent_tickets) || 0), tile: 'dashboard-kpi-icon--red', icon: AlertOctagon, pct: trendPct('urgent'), invert: true },
    { title: 'Ø Lösungszeit', value: fmtMinutes(tk?.avg_resolution_minutes ?? null), tile: 'dashboard-kpi-icon--violet', icon: Clock, pct: null, invert: false },
    { title: 'Zufriedenheit', value: overview?.sla?.resolution_compliance_pct != null ? `${(Number(overview.sla.resolution_compliance_pct) / 20).toFixed(1)} / 5` : '–', tile: 'dashboard-kpi-icon--amber', icon: Smile, pct: null, invert: false },
  ];

  const Card = ({ children, className = '' }: any) => (
    <div className={`dashboard-card ${className}`}>{children}</div>
  );

  const TrendChip = ({ pct, invert }: { pct: number | null; invert: boolean }) => {
    if (pct == null) return <span className="text-xs text-slate-400">Ø {fmtNum(days)} Tage</span>;
    const up = pct >= 0;
    const good = invert ? !up : up;
    const Icon = up ? ArrowUpRight : ArrowDownRight;
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-semibold ${good ? 'text-emerald-600' : 'text-red-500'}`}>
        <Icon className="h-3.5 w-3.5" />
        {Math.abs(pct)}% <span className="font-normal text-slate-400">vs. letzter Zeitraum</span>
      </span>
    );
  };

  return (
    <div className="dashboard-shell flex h-full flex-col gap-3 overflow-hidden p-4 font-sans">
      {/* Header */}
      <div className="dashboard-page-header flex shrink-0 flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-[28px] font-semibold tracking-tight text-slate-900">{t('dashboard:dash.title', 'Übersicht')}</h1>
          <p className="mt-0.5 text-sm text-slate-500">Support-Metriken &amp; Performance</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="dashboard-period-select h-9 w-[170px] text-sm font-medium">
            <Calendar className="mr-2 h-4 w-4 text-slate-400" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">{t('common:x4.last7days', 'Letzte 7 Tage')}</SelectItem>
            <SelectItem value="30">{t('dashboard:dash.last30', 'Letzte 30 Tage')}</SelectItem>
            <SelectItem value="90">{t('common:x4.last90days', 'Letzte 90 Tage')}</SelectItem>
            <SelectItem value="365">{t('common:x4.thisYear', 'Dieses Jahr')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI Grid */}
      <div className="dashboard-kpi-grid grid shrink-0 grid-cols-2 gap-3 lg:grid-cols-5">
        {kpis.map((k) => (
          <Card key={k.title} className="dashboard-kpi-card p-3.5">
            <div className="flex items-center gap-3">
              <span className={`dashboard-kpi-icon flex h-9 w-9 items-center justify-center ${k.tile}`}>
                <k.icon className="h-5 w-5" />
              </span>
              <span className="text-sm font-medium text-slate-500">{k.title}</span>
            </div>
            {overviewLoading ? (
              <Skeleton className="mt-3 h-8 w-20" />
            ) : (
              <div className="dashboard-kpi-value mt-2.5 text-[27px] font-semibold leading-none tracking-tight text-slate-900">{k.value}</div>
            )}
            <div className="mt-2.5">
              <TrendChip pct={k.pct} invert={k.invert} />
            </div>
          </Card>
        ))}
      </div>

      {/* Volumen + Status */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-3">
        <Card className="dashboard-volume-card flex min-h-0 flex-col p-4 lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-slate-900">{t('dashboard:dash.volumeOverTime', 'Ticket-Volumen im Zeitverlauf')}</h3>
              <Info className="h-3.5 w-3.5 text-slate-300" />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-4 text-xs font-medium text-slate-500">
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: BLUE }} />{t('dashboard:dash.total', 'Gesamt')}</span>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: GREEN }} />{t('dashboard:dash.solved', 'Gelöst')}</span>
              </div>
              <span className="flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600">{t('dashboard:dash.daily', 'Täglich')} <ChevronDown className="h-3 w-3" /></span>
            </div>
          </div>
          <div className="min-h-0 w-full flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={safeTrends} margin={{ top: 6, right: 6, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="gGes" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={BLUE} stopOpacity={0.18} /><stop offset="95%" stopColor={BLUE} stopOpacity={0} /></linearGradient>
                  <linearGradient id="gGel" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={GREEN} stopOpacity={0.12} /><stop offset="95%" stopColor={GREEN} stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                <XAxis dataKey="period" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} dy={8}
                  tickFormatter={(v) => { const d = new Date(v); return isNaN(d.getTime()) ? v : `${d.getDate()}. ${['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'][d.getMonth()]}`; }} minTickGap={28} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} width={36} />
                <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', boxShadow: '0 8px 24px -8px rgba(13,28,61,.18)' }} />
                <Area type="monotone" dataKey="created" name="Gesamt" stroke={BLUE} strokeWidth={2.5} fill="url(#gGes)" />
                <Area type="monotone" dataKey="resolved" name="Gelöst" stroke={GREEN} strokeWidth={2} strokeDasharray="5 4" fill="url(#gGel)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="dashboard-status-card flex min-h-0 flex-col p-4">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-base font-semibold text-slate-900">{t('dashboard:dash.statusDist', 'Ticket-Status Verteilung')}</h3>
            <MoreHorizontal className="h-4 w-4 text-slate-300" />
          </div>
          <div className="flex flex-1 items-center gap-2">
            <div className="relative h-[170px] w-[170px] shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={safeStatus} dataKey="count" innerRadius={56} outerRadius={80} paddingAngle={3} stroke="none">
                    {safeStatus.map((s) => <Cell key={s.key} fill={STATUS_META[s.key]?.color || '#cbd5e1'} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-semibold text-slate-900">{fmtNum(statusTotal)}</span>
                <span className="text-[11px] text-slate-400">{t('dashboard:dash.total', 'Gesamt')}</span>
              </div>
            </div>
            <div className="flex-1 space-y-2.5">
              {safeStatus.map((s) => (
                <div key={s.key} className="flex items-center justify-between gap-2 text-sm">
                  <span className="flex items-center gap-2 text-slate-600">
                    <span className="h-2 w-2 rounded-full" style={{ background: STATUS_META[s.key]?.color || '#cbd5e1' }} />
                    {STATUS_META[s.key] ? t(STATUS_META[s.key].key, STATUS_META[s.key].label) : s.key}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900">{fmtNum(s.count)}</span>
                    <span className="w-10 text-right text-xs text-slate-400">{(statusTotal ? (s.count / statusTotal) * 100 : 0).toFixed(1)}%</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
          <button onClick={() => navigate('/tickets')} className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700">
            {t('dashboard:dash.showDetails', 'Details anzeigen')} <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </Card>
      </div>

      {/* Untere Reihe: 4 Panels */}
      <div className="dashboard-bottom-grid grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-4">
        {/* KI-Analyse */}
        <Card className="dashboard-widget-card flex min-h-0 flex-col overflow-hidden p-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-slate-900">{t('dashboard:dash.aiTopics', 'KI-Analyse: Top Themen')}</h3>
              <Info className="h-3.5 w-3.5 text-slate-300" />
            </div>
            <span className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-500">{t('dashboard:dash.allTopics', 'Alle Themen')}</span>
          </div>
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
            {safeTags.map((tag, i) => (
              <div key={tag.id || i} className="flex items-center gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-slate-100 text-xs font-semibold text-slate-500">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="mb-1 truncate text-sm font-medium text-slate-700">{tag.name}</div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(100, (tag.count / maxTag) * 100)}%`, background: BLUE }} />
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2 text-right">
                  <span className="text-sm font-semibold text-slate-700">{fmtNum(tag.count)}</span>
                  <span className="w-8 text-xs text-slate-400">{Math.round(tag.pct)}%</span>
                </div>
              </div>
            ))}
            {safeTags.length === 0 && <div className="py-8 text-center text-sm text-slate-400">{t('common:state.noData', 'Keine Daten')}</div>}
          </div>
        </Card>

        {/* Priorität */}
        <Card className="dashboard-widget-card flex min-h-0 flex-col p-4">
          <div className="mb-2 flex shrink-0 items-center gap-2">
            <h3 className="text-base font-semibold text-slate-900">{t('dashboard:dash.ticketPriority', 'Ticket-Priorität')}</h3>
            <Info className="h-3.5 w-3.5 text-slate-300" />
          </div>
          <div className="flex flex-1 items-center gap-3">
            <div className="relative h-[150px] w-[150px] shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={safePriority} dataKey="count" innerRadius={48} outerRadius={70} paddingAngle={3} stroke="none">
                    {safePriority.map((p) => <Cell key={p.key} fill={PRIORITY_META[p.key]?.color || '#cbd5e1'} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-semibold text-slate-900">{fmtNum(priorityTotal)}</span>
                <span className="text-[10px] text-slate-400">{t('dashboard:dash.total', 'Gesamt')}</span>
              </div>
            </div>
            <div className="flex-1 space-y-2">
              {['urgent', 'high', 'normal', 'low'].map((key) => {
                const p = safePriority.find((x) => x.key === key);
                const count = p?.count || 0;
                return (
                  <div key={key} className="flex items-center justify-between gap-1 text-[13px]">
                    <span className="flex items-center gap-1.5 text-slate-600"><span className="h-2 w-2 rounded-full" style={{ background: PRIORITY_META[key].color }} />{t(PRIORITY_META[key].key, PRIORITY_META[key].label)}</span>
                    <span className="flex items-center gap-1.5"><span className="font-semibold text-slate-900">{fmtNum(count)}</span><span className="w-9 text-right text-[11px] text-slate-400">{(priorityTotal ? (count / priorityTotal) * 100 : 0).toFixed(1)}%</span></span>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>

        {/* Aktuelle Tickets */}
        <Card className="dashboard-widget-card flex min-h-0 flex-col overflow-hidden p-4">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold text-slate-900">{t('dashboard:dash.currentTickets', 'Aktuelle Tickets')}</h3>
            <span onClick={() => navigate('/tickets')} className="cursor-pointer text-xs font-medium text-blue-600">{t('dashboard:dash.showAll', 'Alle anzeigen')}</span>
          </div>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
            {recentTickets.map((tk: any) => (
              <div key={tk.id} onClick={() => navigate(`/tickets/${tk.id}`)} className="flex cursor-pointer items-start gap-3">
                <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${PRIORITY_BADGE[tk.priority] || 'bg-slate-100 text-slate-500'}`}>
                  <Ticket className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-medium text-slate-400">#{tk.ticketNumber}</div>
                  <div className="truncate text-sm font-medium text-slate-800">{tk.subject}</div>
                  <div className="text-[11px] text-slate-400">{tk.requester?.name} · {ago(tk.createdAt)}</div>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${PRIORITY_BADGE[tk.priority] || 'bg-slate-100 text-slate-500'}`}>{PRIORITY_LABEL[tk.priority] ? t(PRIORITY_LABEL[tk.priority].key, PRIORITY_LABEL[tk.priority].label) : tk.priority}</span>
              </div>
            ))}
            {recentTickets.length === 0 && <div className="py-8 text-center text-sm text-slate-400">{t('common:x4.noTickets', 'Keine Tickets')}</div>}
          </div>
          <button onClick={() => navigate('/tickets')} className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700">
            {t('dashboard:dash.showQueue', 'Alle Tickets im Queue anzeigen')} <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </Card>

        {/* Agent Performance */}
        <Card className="dashboard-widget-card flex min-h-0 flex-col overflow-hidden p-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2"><h3 className="text-base font-semibold text-slate-900">{t('dashboard:dash.agentPerf', 'Agent Performance')}</h3><Info className="h-3.5 w-3.5 text-slate-300" /></div>
            <span className="text-xs font-medium text-blue-600">{t('dashboard:dash.allAgents', 'Alle Agenten')}</span>
          </div>
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
            {safeAgents.map((a: any) => (
              <div key={a.id} className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-600">{(a.name || '?').charAt(0).toUpperCase()}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-slate-800">{a.name}</div>
                  <div className="text-[11px] text-slate-400">{t('dashboard:dash.solvedTickets', 'Gelöste Tickets')}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-xs font-semibold text-emerald-600">{a.resolution_rate != null ? `${Math.round(Number(a.resolution_rate))}% SLA` : '—'}</div>
                  <div className="text-[11px] text-slate-400">{fmtNum(Number(a.resolved_tickets) || 0)}</div>
                </div>
              </div>
            ))}
            {safeAgents.length === 0 && <div className="py-8 text-center text-sm text-slate-400">{t('common:x4.noAgents', 'Keine Agenten')}</div>}
          </div>
          <button className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700">
            {t('dashboard:dash.showTeamPerf', 'Team-Performance anzeigen')} <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </Card>
      </div>
    </div>
  );
}

export default Dashboard;
